// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { FiEdit } from "react-icons/fi";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Form } from "react-bootstrap";
import GuardedModal from "../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../global_vars";
import SelectWithInlineCreate from "../common/SelectWithInlineCreate";
import AutocompleteSelect from "../common/AutocompleteSelect";
import { DOCUMENT_STATUS_CHOICES } from "../common/documentStatus";
import { ACCEPTED_DOCUMENT_ACCEPT } from "../common/documentUploadUtils";
import DateInput from "../common/DateInput";
import RichTextEditor from "../common/RichTextEditor";
import { htmlForApi } from "../../utils/richText";
import AddDocumentCategoryModal from "./create/add_document_category";



// Variables
window.Swal = Swal;

// API endpoints
const UPDATE_DOCUMENT = "https://ultima.icsgr.com/api/data_management/document/";

// Edit Document ID Modal
export function EditDocumentIdModal({ document, update_state }) {
  return null;
}

// Edit Document Title Modal
export function EditDocumentTitleModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isValid = title.trim().length >= 2 && title.trim().length <= 40;

  useEffect(() => {
    if (show) {
      setTitle(document.title || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!isValid) {
      Swal.fire("Error", "Title must be between 2 and 40 characters.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { title: title.trim() },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Title updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating title:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update title";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Title</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Title</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Title *:</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                maxLength={40}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Title must be 2–40 characters."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Project Modal
export function EditDocumentProjectModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [project, setProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setProject(document.project?.project_id || "");
      loadProjects();
    }
  }, [show, document]);

  const loadProjects = async () => {
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const response = await axios.get("https://ultima.icsgr.com/api/data_management/all_projects/", { headers: currentHeaders });
      const projectsData = response?.data?.all_projects || response?.data?.results || response?.data?.data || response?.data || [];
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { project_id: project || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Project updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating project:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update project";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Project</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <SelectWithInlineCreate
              label="Project:"
              value={project}
              onChange={setProject}
              placeholderOption="Select Project (Optional)"
              overviewLinkEntity="project"
            >
              {projects.map((proj) => (
                <option key={proj.project_id} value={proj.project_id} title={proj.project_id}>
                  {proj.title}
                </option>
              ))}
            </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: project ? "green" : "red" }}>{project ? "Looks good." : "Project is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!project || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Client Modal
export function EditDocumentClientModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [client, setClient] = useState("");
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setClient(document.client?.client_id || "");
      loadClients();
    }
  }, [show, document]);

  const loadClients = async () => {
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const response = await axios.get("https://ultima.icsgr.com/api/data_management/all_clients/", { headers: currentHeaders });
      const clientsData = response?.data?.all_clients || response?.data?.results || response?.data?.data || response?.data || [];
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { client_id: client || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Client updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating client:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update client";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Client</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <SelectWithInlineCreate
              label="Client:"
              value={client}
              onChange={setClient}
              placeholderOption="Select Client (Optional)"
              overviewLinkEntity="client"
            >
              {clients.map((cli) => (
                <option key={cli.client_id} value={cli.client_id} title={cli.client_id}>
                  {cli.fullname || `${cli.surname} ${cli.name}`.trim() || cli.client_id}
                </option>
              ))}
            </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>{client ? "Looks good." : "No client selected: will clear this field."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Created Modal
export function EditDocumentCreatedModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [created, setCreated] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setCreated(document.created || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!created.trim()) {
      Swal.fire("Error", "Created date is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { created: created },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Created date updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating created date:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update created date";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Created</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Created *</Form.Label>
              <DateInput
                value={created}
                onChange={(e) => setCreated(e.target.value)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: created.trim() ? "green" : "red" }}>{created.trim() ? "Looks good." : "Created date is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!created.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Valid Until Modal
export function EditDocumentValidUntilModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [validuntil, setValidUntil] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setValidUntil(document.validuntil || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!validuntil.trim()) {
      Swal.fire("Error", "Valid until date is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { validuntil: validuntil },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Valid until date updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating valid until date:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update valid until date";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Valid until</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Valid until</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Valid until *</Form.Label>
              <DateInput
                value={validuntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: validuntil.trim() ? "green" : "red" }}>{validuntil.trim() ? "Looks good." : "Valid until date is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!validuntil.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Filepath Modal
export function EditDocumentFilepathModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [filepath, setFilepath] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setFilepath(document.filepath || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!filepath.trim()) {
      Swal.fire("Error", "Filepath is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { filepath: filepath.trim() },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Filepath updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating filepath:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update filepath";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Filepath</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Filepath</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Filepath *:</Form.Label>
              <Form.Control
                type="text"
                value={filepath}
                onChange={(e) => setFilepath(e.target.value)}
                placeholder="Enter filepath"
                maxLength={120}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: filepath.trim() ? "green" : "red" }}>{filepath.trim() ? "Looks good." : "Filepath is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!filepath.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Upload/Replace document file
export function UploadDocumentFileModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => { setShow(false); setFile(null); };
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!file) return;
    setIsLoading(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      delete currentHeaders["Content-Type"]; // allow browser to set multipart boundary
      const formData = new FormData();
      formData.append("filepath", file);
      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        formData,
        { headers: currentHeaders }
      );
      Swal.fire("Success", "File uploaded successfully", "success");
      update_state && update_state(response.data);
      handleClose();
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data?.error || "Failed to upload file";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Upload File</Button>
      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Document File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Select file *</Form.Label>
              <Form.Control
                type="file"
                accept={ACCEPTED_DOCUMENT_ACCEPT}
                onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: file ? "green" : "red" }}>{file ? "Looks good." : "Please select a file."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!file || isLoading}>{isLoading ? "Uploading..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Delete current file (clear filepath)
export function DeleteDocumentFileModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { filepath: "" },
        { headers: currentHeaders }
      );
      Swal.fire("Success", "File removed successfully", "success");
      update_state && update_state(response.data);
      handleClose();
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data?.error || "Failed to remove file";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Delete File</Button>
      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove the stored file for this document?
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "red" }}>This will remove the file reference.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Removing..." : "Confirm"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Original Modal
export function EditDocumentOriginalModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [original, setOriginal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setOriginal(document.original || false);
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { original: original },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Original status updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating original status:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update original status";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Original</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Original Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Check
                type="checkbox"
                label="Is Original Document"
                checked={original}
                onChange={(e) => setOriginal(e.target.checked)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Trafficable Modal
export function EditDocumentTrafficableModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [trafficable, setTrafficable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setTrafficable(document.trafficable || false);
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { trafficable: trafficable },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Trafficable status updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating trafficable status:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update trafficable status";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Trafficable</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Trafficable Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Check
                type="checkbox"
                label="Is Trafficable Document"
                checked={trafficable}
                onChange={(e) => setTrafficable(e.target.checked)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Status Modal
export function EditDocumentStatusModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const statusChoices = [
    { value: "", label: "No Status" },
    ...DOCUMENT_STATUS_CHOICES,
  ];

  useEffect(() => {
    if (show) {
      setStatus(document.status || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { status: status || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Status updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating status:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update status";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Status</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <AutocompleteSelect value={status} onChange={setStatus}>
                {statusChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </AutocompleteSelect>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Document Notes Modal
export function EditDocumentNotesModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setNotes(document.notes || "");
    }
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { notes: htmlForApi(notes) },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Notes updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating notes:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update notes";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Notes</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Notes:</Form.Label>
              <RichTextEditor
                rows={3}
                value={notes}
                onChange={setNotes}
                placeholder="Enter notes"
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditDocumentCategoryModal({ document, update_state }) {
  const [show, setShow] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCategoryId(document?.category?.doccate_id || "");
    const load = async () => {
      try {
        const currentHeaders = {
          ...headers,
          Authorization: "Token " + localStorage.getItem("userToken"),
        };
        const res = await axios.get(
          "https://ultima.icsgr.com/api/administration/all_document_categories/",
          { headers: currentHeaders }
        );
        const list = res?.data?.all_document_categories || [];
        setCategories(Array.isArray(list) ? list.filter((c) => c.active !== false) : []);
      } catch (_e) {
        setCategories([]);
      }
    };
    load();
  }, [show, document]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        Authorization: "Token " + localStorage.getItem("userToken"),
      };
      const response = await axios.put(
        UPDATE_DOCUMENT + document.document_id + "/",
        { category_id: categoryId || "" },
        { headers: currentHeaders }
      );
      Swal.fire("Success", "Category updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      const apiMsg =
        e?.response?.data?.error ||
        e?.response?.data?.category_id?.[0] ||
        e?.response?.data?.detail ||
        "Failed to update category";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}>
        <FiEdit style={{ marginRight: 6 }} /> Edit Category
      </Button>

      <GuardedModal show={show} onHide={handleClose} centered enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SelectWithInlineCreate
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            placeholderOption="Uncategorized"
            overviewLinkEntity="document_category"
            onAddClick={() => setAddCategoryOpen(true)}
          >
            {categories.map((c) => (
              <option key={c.doccate_id} value={c.doccate_id}>
                {c.title}
              </option>
            ))}
          </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddDocumentCategoryModal
        nested
        hideTrigger
        show={addCategoryOpen}
        onHide={() => setAddCategoryOpen(false)}
        onDocumentCategoryCreated={(created) => {
          if (created?.doccate_id) {
            setCategories((prev) => {
              const next = [
                ...prev.filter((c) => c.doccate_id !== created.doccate_id),
                created,
              ];
              next.sort(
                (a, b) =>
                  (a.orderindex ?? 0) - (b.orderindex ?? 0) ||
                  String(a.title).localeCompare(String(b.title))
              );
              return next;
            });
            setCategoryId(created.doccate_id);
          }
          setAddCategoryOpen(false);
        }}
      />
    </>
  );
}
